import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddonsPageService } from 'src/app/components/addons-page/addons-page.service';
import { BillingApiData } from 'src/app/pages/new-billing/types/api.types';
import {
  CHOOSE_PLAN_INTENT_ADD_CARD,
  CHOOSE_PLAN_INTENT_QUERY_PARAM,
  EXPLICIT_PLAN_SELECTION_FEATURE,
  resolveExplicitPlanSelectionVariation
} from 'src/app/pages/new-billing/utils/explicit-plan-selection.utils';
import { GrowthBookService } from 'src/app/services/growthbook/service';
import { ExperimentProps } from 'src/app/services/growthbook/types';
import { LocalizedPricingService } from 'src/app/services/localized-pricing/localized-pricing.service';
import { provideTrackingCtx } from 'src/app/services/tracking/context';
import { AuthState } from 'src/app/store/auth/auth.state';
import { ConcretePlan } from '../models/plan.model';

@UntilDestroy()
@Component({
  selector: 'app-billing-plans-section',
  templateUrl: './billing-plans-section.component.html',
  styleUrls: ['./billing-plans-section.component.scss'],
  providers: [
    provideTrackingCtx({ location: 'billing details' }),
  ],
})
export class BillingPlansSectionComponent implements OnInit {
  @Input()
  set billingData(value: BillingApiData) {
    const { userCount } = this.store.selectSnapshot(AuthState.company);
    this.currentPlan = new ConcretePlan(value, userCount, this.growthBookService.getFeatureValue('billing:discount_removal_companies', false), this.growthBookService, this.localizedPricingService);
    this.syncCardEntryExperimentTitle();
  }

  @Input() isV2 = false;
  @Input() hideBackButton = false;

  /** Billing v2 explicit-plan-selection B/C: alternate titles on Choose Your Plan (card-entry flow). */
  useCardEntryExperimentTitle = false;

  private explicitPlanCardEntryTitleEligible = false;
  private choosePlanIntentIsAddCard = false;

  @Output()
  backToBillingDetails = new EventEmitter<void>();

  @Output()
  addonChange = new EventEmitter<void>();

  currentPlan: ConcretePlan;

  constructor(
    private store: Store,
    private growthBookService: GrowthBookService,
    private readonly localizedPricingService: LocalizedPricingService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    public readonly addonsPageService: AddonsPageService,
  ) { }

  ngOnInit(): void {
    combineLatest([
      this.activatedRoute.queryParamMap.pipe(
        map(params => ({
          tdInternal: params.get('td_internal'),
          choosePlanIntent: params.get(CHOOSE_PLAN_INTENT_QUERY_PARAM),
        })),
      ),
      this.growthBookService.getFeature<ExperimentProps>(EXPLICIT_PLAN_SELECTION_FEATURE),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([{ tdInternal, choosePlanIntent }]) => {
        const variation = resolveExplicitPlanSelectionVariation(this.growthBookService, tdInternal);
        this.explicitPlanCardEntryTitleEligible = variation === 'B' || variation === 'C';
        this.choosePlanIntentIsAddCard = choosePlanIntent === CHOOSE_PLAN_INTENT_ADD_CARD;
        this.syncCardEntryExperimentTitle();
      });
  }

  private syncCardEntryExperimentTitle(): void {
    this.useCardEntryExperimentTitle =
      this.explicitPlanCardEntryTitleEligible &&
      this.choosePlanIntentIsAddCard &&
      !this.hasExistingPaymentMethod;
  }

  public goBackToWidgetsCenter() {
    const urlParam = this.activatedRoute.snapshot.queryParamMap.get('url');
    if (urlParam) {
      this.router.navigate([urlParam]);
    } else {
      this.backToBillingDetails.emit();
    }
  }

  get hasExistingPaymentMethod(): boolean {
    return !!this.currentPlan?.paymentModel?.cardLast4;
  }

  handleAddonChange(): void {
    // Emit event to parent component to handle billing data refresh
    this.addonChange.emit();
  }
}
